import { ExchangeRecord } from "../../reducers/exchange/exchanges";

export interface FilterLoyaltyRedemptionOptionsParams {
  exchanges: ExchangeRecord[];
  loyaltyCampaignId?: string | null;
  loyaltyCurrency?: string | null;
}

export interface FilterRewardStoreRedemptionOptionsParams {
  exchanges: ExchangeRecord[];
  widgetLedgerCurrency: string;
}

/**
 * Filters exchanges for a Loyalty Dashboard widget.
 *
 * Rules (applied in order):
 *   1. Exchanges carrying a `rewardStoreId` are never loyalty exchanges; drop them.
 *   2. If a loyalty currency is known, require the exchange's `ledgerCurrency`
 *      to match (trim-insensitive). Exchanges with no `ledgerCurrency` are
 *      kept (backward compatibility for legacy records).
 *   3. If a loyalty campaignId is known AND the exchange carries a campaignId,
 *      they must match. Exchanges without a campaignId are kept (legacy
 *      records predate the field being written).
 *
 * This is intentionally stricter than a campaignId-only filter because some
 * legacy exchanges do not persist campaignId but still must not cross-pollute
 * between loyalty currency and reward-store currency.
 */
export function filterLoyaltyRedemptionOptions({
  exchanges,
  loyaltyCampaignId,
  loyaltyCurrency,
}: FilterLoyaltyRedemptionOptionsParams): ExchangeRecord[] {
  const currencyTrimmed = (loyaltyCurrency || "").trim().toLowerCase();

  return exchanges.filter((ex) => {
    if (ex.rewardStoreId) {
      return false;
    }

    if (
      currencyTrimmed &&
      ex.ledgerCurrency &&
      ex.ledgerCurrency.trim().toLowerCase() !== currencyTrimmed
    ) {
      return false;
    }

    if (loyaltyCampaignId && ex.campaignId && ex.campaignId !== loyaltyCampaignId) {
      return false;
    }

    return true;
  });
}

/**
 * Filters exchanges for a Reward Store widget. Matches only by the widget's
 * configured ledger currency; backend already scopes by `rewardStoreWidgetId`.
 * Exchanges missing `ledgerCurrency` are kept (backward compat).
 */
export function filterRewardStoreRedemptionOptions({
  exchanges,
  widgetLedgerCurrency,
}: FilterRewardStoreRedemptionOptionsParams): ExchangeRecord[] {
  const currencyTrimmed = widgetLedgerCurrency.trim().toLowerCase();

  return exchanges.filter(
    (ex) =>
      !ex.ledgerCurrency ||
      ex.ledgerCurrency.trim().toLowerCase() === currencyTrimmed
  );
}
